import {
  ApolloClient,
  ApolloProvider,
  NormalizedCacheObject,
} from "@apollo/client";
import { browserTracingIntegration } from "@sentry/browser";
import * as Sentry from "@sentry/react";
import { LocalStorageWrapper, persistCache } from "apollo3-cache-persist";
import "intersection-observer";
import moment from "moment";
import { useEffect, useState } from "react";
import { Root, createRoot } from "react-dom/client";
import { BrowserRouter, Route, Switch } from "react-router-dom";

import ExtensionRoutes from "@apps/extension/routes";
import MainApp from "@apps/main/app";
import MeetAddon from "@apps/meet-addon/meet-addon";
import OnboardingLayout from "@apps/onboarding/components/layout";
import Onboarding from "@apps/onboarding/onboarding";
import cache, { ignoredCacheKeys } from "@cache/cache";
import Container from "@components/container/container";
import Loading from "@components/loading/loading";
import ScrollToTop from "@components/scroll-top/scroll-top";
import customApolloClient from "@graphql/client";
import { apolloPersistCacheKey } from "@helpers/constants";
import { addSentryEventContext } from "@helpers/sentry-event";

if (!window.Cypress) {
  if (!import.meta.env.DEV) {
    Sentry.init({
      dsn: import.meta.env.DEV
        ? "https://049612b8042e4796b0bb85596ec1696f@o1012718.ingest.sentry.io/6231090"
        : "https://b86026f89303448088e0bd6dfefe3047@o1012718.ingest.sentry.io/6041535",
      integrations: [browserTracingIntegration()],
      tracesSampleRate: 0.1,
      debug: import.meta.env.VITE_SENTRY_SHOW_LOG === "1",
      release: window.currentCommitHash,
      // Lab and preview builds use the production DSN, so tag them by host.
      environment:
        window.location.hostname === "app.topicflow.com"
          ? "production"
          : window.location.hostname,
      // Same list as the backend IGNORED_GRAPHENE_ERROR_MESSAGES. Anchored,
      // because the backend matches with startswith().
      ignoreErrors: [
        /^Invalid id\b/,
        /^You do not have permission to access the instance with id/,
      ],
      beforeSend: addSentryEventContext,
    });
  }

  if (!import.meta.env.DEV && !window.TF.hijacked && window.Intercom) {
    window.Intercom("boot", {
      api_base: "https://api-iam.intercom.io",
      app_id: window.intercomAppId,
      alignment: "left",
    });
  }
}

const TopicflowApp = () => {
  const [client, setClient] = useState<
    ApolloClient<NormalizedCacheObject> | undefined
  >();
  useEffect(() => {
    if (window.TF.hijacked) {
      setClient(customApolloClient);
    } else {
      async function initCache() {
        await persistCache({
          cache,
          storage: new LocalStorageWrapper(window.localStorage),
          key: apolloPersistCacheKey,
          persistenceMapper: async (data: any) => {
            const dateOneMonthAgo = moment
              .utc()
              .subtract(1, "month")
              .format("YYYY-MM-DD[T]HH:mm:ss[+00:00]");
            try {
              const parsedData = JSON.parse(data);
              const filteredData = Object.entries(parsedData)
                .filter(
                  ([key]) =>
                    !ignoredCacheKeys.find((ignoredCacheKey) =>
                      key.includes(ignoredCacheKey),
                    ),
                )
                .filter(([key, value]: any[]) => {
                  if (key.includes("BasicMeetingNode")) {
                    return value.endDatetime > dateOneMonthAgo;
                  }
                  return true;
                });
              return JSON.stringify(Object.fromEntries(filteredData));
            } catch (e) {
              window.console.error(e);
            }
            return "{}";
          },
        });
        setClient(customApolloClient);
      }
      initCache();
    }
  }, []);

  if (!client) {
    return (
      <Container>
        <Loading />
      </Container>
    );
  }

  return (
    <ApolloProvider client={client}>
      <BrowserRouter>
        <ScrollToTop />
        <Switch>
          <Route path="/extension">
            <ExtensionRoutes />
          </Route>
          <Route path="/onboarding">
            <OnboardingLayout>
              <Onboarding />
            </OnboardingLayout>
          </Route>
          <Route path="/meet-addon">
            <MeetAddon />
          </Route>
          <Route path="*">
            <MainApp />
          </Route>
        </Switch>
      </BrowserRouter>
    </ApolloProvider>
  );
};

// Plain markup only: the crash can come from the providers or the cache.
const AppCrashFallback = () => (
  <div className="p-8 text-center text-gray-700">
    <div className="font-medium">Something went wrong.</div>
    <button
      type="button"
      className="mt-4 underline text-blue-600"
      onClick={() => {
        // Bad cached data can cause the crash, so do not restore it.
        window.localStorage?.removeItem(apolloPersistCacheKey);
        window.location.reload();
      }}
    >
      Reload the page
    </button>
  </div>
);

const RootApp = () => (
  <Sentry.ErrorBoundary fallback={<AppCrashFallback />}>
    <TopicflowApp />
  </Sentry.ErrorBoundary>
);

// Store root on window to persist across HMR updates
declare global {
  interface Window {
    __reactRoot?: Root;
  }
}

const container = document.getElementById("root");
if (!container) {
  throw new Error("Root element not found");
}

// Only create root once
if (!window.__reactRoot) {
  window.__reactRoot = createRoot(container);
}

// Initial render
window.__reactRoot.render(<RootApp />);

// HMR: just re-render, don't recreate root
if (import.meta.hot) {
  import.meta.hot.accept(() => {
    window.__reactRoot?.render(<RootApp />);
  });
}

export default TopicflowApp;
